import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  MenuItem,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

const API_URL = "https://htm.bayex.lk/api/save_billing_book.php";

const BillingBook = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Billing date selected by the user.
  const [billDate, setBillDate] = useState(new Date().toISOString().split('T')[0]);

  // Reference total sales today
  const [totalSalesToday, setTotalSalesToday] = useState('');

  // Pre‑built extras
  const [totalTips, setTotalTips] = useState('');
  const [totalServiceCharges, setTotalServiceCharges] = useState('');

  // Custom entries
  const [entries, setEntries] = useState([]);

  // Loading state
  const [loading, setLoading] = useState(false);

  // Entry modal
  const [openEntryModal, setOpenEntryModal] = useState(false);
  const [newEntry, setNewEntry] = useState({ type: 'income', amount: '', description: '' });
  const [entryEditIndex, setEntryEditIndex] = useState(-1);

  // Summary modal
  const [openSummaryModal, setOpenSummaryModal] = useState(false);

  // ────────────────────────────────────────────────────────
  // Compute all your new stats here
  // ────────────────────────────────────────────────────────
  const refTotal     = parseFloat(totalSalesToday) || 0;
  const incomeTotal  = entries
    .filter(e => e.type === 'income')
    .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const expenseTotal = entries
    .filter(e => e.type === 'expense')
    .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

  // Net Revenue = Sales + Extras − Expenses
  const netRevenue   = refTotal + incomeTotal - expenseTotal;

  // Percent‑of‑sales breakdown
  const incomePct    = refTotal > 0 ? (incomeTotal  / refTotal) * 100 : 0;
  const expensePct   = refTotal > 0 ? (expenseTotal / refTotal) * 100 : 0;

  // Cash on Cashier (if you still need it)
  const cashOnCashier = incomeTotal - expenseTotal;

  // ────────────────────────────────────────────────────────
  // Fetch on date change
  // ────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}?summary_date=${billDate}`)
      .then(r => r.json())
      .then(data => {
        if (data.bill_date) {
          setTotalSalesToday(data.total_sales_today);
          setTotalTips(data.total_tips ?? '');
          setTotalServiceCharges(data.total_service_charges ?? '');
          setEntries(data.entries ? JSON.parse(data.entries) : []);
        } else {
          setTotalSalesToday('');
          setTotalTips('');
          setTotalServiceCharges('');
          setEntries([]);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [billDate]);

  // Handlers for entry modal
  const openEntry = idx => {
    if (idx >= 0) {
      setNewEntry(entries[idx]);
      setEntryEditIndex(idx);
    } else {
      setNewEntry({ type: 'income', amount: '', description: '' });
      setEntryEditIndex(-1);
    }
    setOpenEntryModal(true);
  };
  const closeEntry = () => {
    setOpenEntryModal(false);
    setEntryEditIndex(-1);
  };
  const saveEntry = () => {
    if (!newEntry.amount) return alert("Enter amount");
    if (entryEditIndex >= 0) {
      const arr = [...entries];
      arr[entryEditIndex] = newEntry;
      setEntries(arr);
    } else {
      setEntries([...entries, newEntry]);
    }
    closeEntry();
  };
  const deleteEntry = idx => {
    const arr = [...entries];
    arr.splice(idx, 1);
    setEntries(arr);
  };

  // Save full record
  const saveRecord = () => {
    const payload = {
      bill_date:             billDate,
      total_sales_today:     refTotal,
      total_tips:            parseFloat(totalTips) || 0,
      total_service_charges: parseFloat(totalServiceCharges) || 0,
      entries:               entries,
      cash_on_cashier:       cashOnCashier,
    };
    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(r => r.json())
      .then(d => {
        if (d.status === 'success') {
          alert("Saved successfully");
        } else {
          alert("Error: " + d.message);
        }
      })
      .catch(() => alert("Network error"));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>Billing Book</Typography>

      {/* Date selector */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', my: 2 }}>
        <TextField
          label="Billing Date"
          type="date"
          value={billDate}
          onChange={e => setBillDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button startIcon={<RefreshIcon />} onClick={() => { /* force re-fetch */ setBillDate(billDate) }} variant="contained">
          Refresh
        </Button>
      </Box>

      {loading && (
        <Box sx={{ textAlign: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Reference total sales */}
      <Paper sx={{ p:2, mb:2 }}>
        <Typography variant="h6">Total Sales Today (Ref.)</Typography>
        <TextField
          fullWidth
          variant="standard"
          type="number"
          value={totalSalesToday}
          onChange={e => setTotalSalesToday(e.target.value)}
        />
      </Paper>

      {/* Pre‑built extras */}
      <Paper sx={{ p:2, mb:2, display:'flex', gap:4, flexWrap:'wrap' }}>
        <Box>
          <Typography>Total Tips</Typography>
          <TextField
            variant="standard"
            type="number"
            value={totalTips}
            onChange={e => setTotalTips(e.target.value)}
          />
          <Button sx={{ mt:1 }} onClick={() => {
            if (totalTips) {
              setEntries(es => [...es, { type:'income', amount:totalTips, description:'Total Tips' }]);
            }
          }}>Add Tips Entry</Button>
        </Box>
        <Box>
          <Typography>Total Service Charges</Typography>
          <TextField
            variant="standard"
            type="number"
            value={totalServiceCharges}
            onChange={e => setTotalServiceCharges(e.target.value)}
          />
          <Button sx={{ mt:1 }} onClick={() => {
            if (totalServiceCharges) {
              setEntries(es => [...es, { type:'income', amount:totalServiceCharges, description:'Total Service Charges' }]);
            }
          }}>Add Service Charge</Button>
        </Box>
      </Paper>

      {/* ─── NEW OVERVIEW PANEL ───────────────────────────── */}
      <Paper sx={{ p:2, mb:2 }}>
        <Typography variant="h6">Overview</Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            textAlign: 'center',
            mt: 1,
            flexWrap: 'wrap',
          }}
        >
          {[
            { label: 'Total Sales',    value: refTotal,    caption: 'Ref.' },
            { label: 'Extras (Income)', value: incomeTotal, caption: `${incomePct.toFixed(1)}%` },
            { label: 'Expenses',        value: expenseTotal,caption: `${expensePct.toFixed(1)}%` },
            { label: 'Net Revenue',     value: netRevenue,  caption: null },
          ].map(stat => (
            <Box key={stat.label} sx={{ m:1, minWidth: 100 }}>
              <Typography variant="subtitle2">{stat.label}</Typography>
              <Typography variant="h5">Rs {stat.value.toFixed(2)}</Typography>
              {stat.caption && (
                <Typography variant="caption" color="text.secondary">
                  {stat.caption}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Entries */}
      <Paper sx={{ p:2, mb:2 }}>
        <Typography variant="h6">Entries</Typography>
        {entries.length === 0 ? (
          <Typography>No entries</Typography>
        ) : isMobile ? (
          <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
            {entries.map((e,i) => (
              <Paper key={i} sx={{ p:1 }}>
                <Typography>{e.description} ({e.type})</Typography>
                <Typography>Rs {parseFloat(e.amount).toFixed(2)}</Typography>
                <Button size="small" onClick={()=>openEntry(i)}>Edit</Button>
                <Button size="small" color="error" onClick={()=>deleteEntry(i)}>Delete</Button>
              </Paper>
            ))}
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e,i) => (
                <TableRow key={i}>
                  <TableCell>{e.type}</TableCell>
                  <TableCell>{e.description}</TableCell>
                  <TableCell>{parseFloat(e.amount).toFixed(2)}</TableCell>
                  <TableCell>
                    <Button onClick={()=>openEntry(i)}>Edit</Button>
                    <Button color="error" onClick={()=>deleteEntry(i)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Button sx={{ mt:1 }} variant="contained" onClick={()=>openEntry(-1)}>
          Add New Entry
        </Button>
      </Paper>

      {/* Cash on Cashier (optional) */}
      <Paper sx={{ p:2, mb:2 }}>
        <Typography variant="h6">Cash on Cashier</Typography>
        <Typography variant="h5">Rs {cashOnCashier.toFixed(2)}</Typography>
      </Paper>

      {/* Check summary */}
      <Button variant="contained" onClick={()=>setOpenSummaryModal(true)}>
        Check Cash
      </Button>

      {/* Save record */}
      <Box sx={{ mt:2 }}>
        <Button variant="contained" onClick={saveRecord}>
          Save Full Records
        </Button>
      </Box>

      {/* Summary Modal */}
      <Dialog open={openSummaryModal} onClose={()=>setOpenSummaryModal(false)} fullWidth>
        <DialogTitle>Summary</DialogTitle>
        <DialogContent dividers>
          <Typography>Total Sales (ref): Rs {refTotal.toFixed(2)}</Typography>
          <Typography>Extras: Rs {incomeTotal.toFixed(2)} ({incomePct.toFixed(1)}%)</Typography>
          <Typography>Expenses: Rs {expenseTotal.toFixed(2)} ({expensePct.toFixed(1)}%)</Typography>
          <Typography variant="h6">Net Revenue: Rs {netRevenue.toFixed(2)}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpenSummaryModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Entry Modal */}
      <Dialog open={openEntryModal} onClose={closeEntry} fullWidth>
        <DialogTitle>{entryEditIndex>=0? 'Edit Entry' : 'Add Entry'}</DialogTitle>
        <DialogContent sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          <TextField
            select
            name="type"
            label="Type"
            variant="standard"
            value={newEntry.type}
            onChange={e=>setNewEntry(ne=>({...ne,type:e.target.value}))}
          >
            <MenuItem value="income">Income</MenuItem>
            <MenuItem value="expense">Expense</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </TextField>
          <TextField
            name="description"
            label="Description"
            variant="standard"
            value={newEntry.description}
            onChange={e=>setNewEntry(ne=>({...ne,description:e.target.value}))}
          />
          <TextField
            name="amount"
            type="number"
            label="Amount"
            variant="standard"
            value={newEntry.amount}
            onChange={e=>setNewEntry(ne=>({...ne,amount:e.target.value}))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEntry}>Cancel</Button>
          <Button onClick={saveEntry} variant="contained">
            {entryEditIndex>=0? 'Update':'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingBook;
